export const BUDGET_VIEW_STATE_QUERY_PARAM = 'view_state';

const VIEW_STATE_VERSION = 1;
const MAX_ENCODED_VIEW_STATE_LENGTH = 12000;
const MAX_HIERARCHY_DEPTH = 20;

const isPrimitiveId = value =>
  typeof value === 'string' || typeof value === 'number';

const truncateText = (value, maxLength) => {
  if (value === null || value === undefined) return undefined;
  return String(value).slice(0, maxLength);
};

const getRuntime = () => (typeof window !== 'undefined' ? window : {});

export const normalizeBudgetDeepLinkViewState = (viewState, budgetId) => {
  if (!viewState || typeof viewState !== 'object') return null;
  if (viewState.version !== VIEW_STATE_VERSION) return null;
  if (!isPrimitiveId(viewState.budgetId)) return null;
  if (
    budgetId !== null &&
    budgetId !== undefined &&
    viewState.budgetId.toString() !== budgetId.toString()
  ) {
    return null;
  }

  const hierarchy = Array.isArray(viewState.hierarchy)
    ? viewState.hierarchy
        .slice(0, MAX_HIERARCHY_DEPTH)
        .map(item => {
          if (
            !item ||
            !isPrimitiveId(item.id) ||
            typeof item.type !== 'string'
          ) {
            return null;
          }

          return {
            id: item.id,
            name: truncateText(item.name, 150),
            type: item.type.slice(0, 32),
          };
        })
        .filter(Boolean)
    : [];

  const panel =
    viewState.panel && viewState.panel.open
      ? {
          open: true,
          tab: ['comments', 'rate-card', 'assistant'].includes(
            viewState.panel.tab
          )
            ? viewState.panel.tab
            : 'comments',
        }
      : null;

  if (!hierarchy.length && !panel) return null;

  const detail =
    viewState.detail &&
    isPrimitiveId(viewState.detail.itemId) &&
    Number.isFinite(Number(viewState.detail.level))
      ? {
          itemId: viewState.detail.itemId,
          level: Number(viewState.detail.level),
          type: truncateText(viewState.detail.type, 32),
        }
      : null;

  const cell =
    viewState.cell &&
    isPrimitiveId(viewState.cell.rowId) &&
    typeof viewState.cell.columnKey === 'string'
      ? {
          rowId: viewState.cell.rowId,
          columnKey: viewState.cell.columnKey.slice(0, 64),
          detailItemId: isPrimitiveId(viewState.cell.detailItemId)
            ? viewState.cell.detailItemId
            : detail?.itemId,
          detailLevel: Number.isFinite(Number(viewState.cell.detailLevel))
            ? Number(viewState.cell.detailLevel)
            : detail?.level,
          detailSheetType: truncateText(
            viewState.cell.detailSheetType || detail?.type,
            32
          ),
          openComment: Boolean(viewState.cell.openComment),
        }
      : null;

  const comment =
    viewState.comment && isPrimitiveId(viewState.comment.id)
      ? {
          id: viewState.comment.id,
          type: viewState.comment.type === 'cell' ? 'cell' : 'budget',
        }
      : null;

  const normalized = {
    version: VIEW_STATE_VERSION,
    budgetId: viewState.budgetId,
    savedAt: truncateText(viewState.savedAt || viewState.saved_at, 64),
    hierarchy,
    detail,
    cell,
  };
  if (panel) normalized.panel = panel;
  if (comment) normalized.comment = comment;
  if (isPrimitiveId(viewState.notificationId)) {
    normalized.notificationId = viewState.notificationId;
  }
  return normalized;
};

const stringToBytes = value => {
  const runtime = getRuntime();
  if (typeof runtime.TextEncoder === 'function') {
    return new runtime.TextEncoder().encode(value);
  }

  const encoded = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hex) => String.fromCharCode(parseInt(hex, 16))
  );
  return Uint8Array.from(encoded, char => char.charCodeAt(0));
};

const bytesToString = bytes => {
  const runtime = getRuntime();
  if (typeof runtime.TextDecoder === 'function') {
    return new runtime.TextDecoder().decode(bytes);
  }

  const encoded = Array.from(bytes)
    .map(byte => `%${byte.toString(16).padStart(2, '0')}`)
    .join('');
  return decodeURIComponent(encoded);
};

const bytesToBase64 = bytes => {
  const runtime = getRuntime();
  if (typeof runtime.btoa === 'function') {
    let binary = '';
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte);
    });
    return runtime.btoa(binary);
  }

  throw new Error('No base64 encoder available');
};

const base64ToBytes = value => {
  const runtime = getRuntime();
  if (typeof runtime.atob === 'function') {
    const binary = runtime.atob(value);
    return Uint8Array.from(binary, char => char.charCodeAt(0));
  }

  throw new Error('No base64 decoder available');
};

const toBase64Url = value =>
  bytesToBase64(stringToBytes(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

const fromBase64Url = value => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  );
  return bytesToString(base64ToBytes(padded));
};

export const encodeBudgetViewStateForUrl = (viewState, budgetId) => {
  const normalized = normalizeBudgetDeepLinkViewState(viewState, budgetId);
  if (!normalized) return '';

  try {
    const encoded = toBase64Url(JSON.stringify(normalized));
    return encoded.length <= MAX_ENCODED_VIEW_STATE_LENGTH ? encoded : '';
  } catch (error) {
    return '';
  }
};

export const decodeBudgetViewStateFromUrl = (encodedViewState, budgetId) => {
  if (
    !encodedViewState ||
    typeof encodedViewState !== 'string' ||
    encodedViewState.length > MAX_ENCODED_VIEW_STATE_LENGTH
  ) {
    return null;
  }

  try {
    const parsed = JSON.parse(fromBase64Url(encodedViewState));
    return normalizeBudgetDeepLinkViewState(parsed, budgetId);
  } catch (error) {
    return null;
  }
};

export const buildPrivateBudgetShareUrl = ({
  appBaseUrl,
  budgetId,
  viewState,
}) => {
  if (!appBaseUrl || !budgetId) return '';

  const params = new URLSearchParams({ budget: budgetId.toString() });
  const encodedViewState = encodeBudgetViewStateForUrl(viewState, budgetId);
  if (encodedViewState) {
    params.set(BUDGET_VIEW_STATE_QUERY_PARAM, encodedViewState);
  }

  return `${appBaseUrl.replace(/\/+$/g, '')}/home?${params.toString()}`;
};
